import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Pencil, Trash2, X, Power, Package } from "lucide-react";
import {
  USER_TYPES,
  getAllProducts,
  getProductsByUserType,
  createProduct,
  updateProduct,
  toggleProductStatus,
  deleteProduct,
  normalizeProduct,
  toPayload,
  formatDuration,
  planLimits,
} from "../../services/subscriptionProductService";
import "./Subscription.css";

const emptyForm = {
  name: "",
  code: "",
  featureCode: "",
  description: "",
  price: "",
  validityDays: "",
  userType: USER_TYPES[0],
  active: true,
  jobPostLimit: "",
  jobInviteLimit: "",
  profileViewLimit: "",
  resumeLimit: "",
};

const limitStr = (v) => (v === null || v === undefined ? "" : String(v));

function SubscriptionProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data =
        typeFilter === "ALL"
          ? await getAllProducts()
          : await getProductsByUserType(typeFilter);
      setProducts((Array.isArray(data) ? data : []).map(normalizeProduct));
    } catch (error) {
      setProducts([]);
      showToast("error", error.message || "Unable to load products");
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      const matchSearch = !q || `${p.name} ${p.code} ${p.description}`.toLowerCase().includes(q);
      const matchStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" ? p.active : !p.active);
      return matchSearch && matchStatus;
    });
  }, [products, search, statusFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      code: p.code,
      featureCode: p.featureCode,
      description: p.description,
      price: String(p.price),
      validityDays: String(p.duration || ""),
      userType: p.userType || USER_TYPES[0],
      active: p.active,
      jobPostLimit: limitStr(p.jobPostLimit),
      jobInviteLimit: limitStr(p.jobInviteLimit),
      profileViewLimit: limitStr(p.profileViewLimit),
      resumeLimit: limitStr(p.resumeLimit),
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  const setField = (key) => (e) =>
    setForm((prev) => ({
      ...prev,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setFormError("Product name is required.");
    if (form.price === "" || Number(form.price) < 0)
      return setFormError("Enter a valid price.");
    if (!form.validityDays || Number(form.validityDays) <= 0)
      return setFormError("Validity must be at least 1 day.");

    try {
      setSaving(true);
      setFormError("");
      if (editingId) {
        await updateProduct(editingId, toPayload(form));
        showToast("success", "Product updated");
      } else {
        await createProduct(toPayload(form));
        showToast("success", "Product created");
      }
      setModalOpen(false);
      loadProducts();
    } catch (error) {
      setFormError(error.message || "Unable to save product");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    try {
      const updated = normalizeProduct(await toggleProductStatus(p.id));
      setProducts((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
      showToast("success", `${p.name} is now ${updated.active ? "active" : "inactive"}`);
    } catch (error) {
      showToast("error", error.message || "Unable to change status");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await deleteProduct(p.id);
      setProducts((prev) => prev.filter((x) => x.id !== p.id));
      showToast("success", "Product deleted");
    } catch (error) {
      showToast("error", error.message || "Unable to delete product");
    }
  };

  const activeCount = products.filter((p) => p.active).length;

  return (
    <div className="sp-page">
      <header className="sp-head">
        <div>
          <span className="sp-kicker">Subscriptions</span>
          <h1>Subscription Products</h1>
          <p>Create and manage the plans partners and trainers can subscribe to.</p>
        </div>
        <button className="sp-btn sp-btn-primary" onClick={openCreate}>
          <Plus size={17} /> Add Product
        </button>
      </header>

      <div className="sp-stats">
        <div><strong>{products.length}</strong><span>Total products</span></div>
        <div><strong>{activeCount}</strong><span>Active</span></div>
        <div><strong>{products.length - activeCount}</strong><span>Inactive</span></div>
      </div>

      <div className="sp-toolbar">
        <div className="sp-search">
          <Search size={16} />
          <input
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="sp-tabs">
          {["ALL", ...USER_TYPES].map((t) => (
            <button
              key={t}
              className={typeFilter === t ? "active" : ""}
              onClick={() => setTypeFilter(t)}
            >
              {t === "ALL" ? "All" : t.charAt(0) + t.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">Any status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
      </div>

      <div className="sp-card">
        {loading ? (
          <div className="sp-state"><div className="sp-spinner" /><p>Loading products...</p></div>
        ) : visible.length === 0 ? (
          <div className="sp-state">
            <Package size={42} />
            <h3>No products found</h3>
            <p>Add a subscription product or change your filters.</p>
          </div>
        ) : (
          <div className="sp-table-wrap">
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>For</th>
                  <th>Price</th>
                  <th>Validity</th>
                  <th>Limits</th>
                  <th>Status</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.name}</strong>
                      {p.code && <code className="sp-code">{p.code}</code>}
                      {p.description && <small>{p.description}</small>}
                    </td>
                    <td><span className="sp-chip">{p.userType || "—"}</span></td>
                    <td className="sp-price">₹{p.price.toLocaleString("en-IN")}</td>
                    <td>{formatDuration(p.duration)}</td>
                    <td>
                      <div className="sp-limits">
                        {planLimits(p).map((l) => (
                          <span key={l.key} title={l.label}>{l.short}: <b>{l.value}</b></span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`sp-badge ${p.active ? "on" : "off"}`}>
                        {p.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="sp-actions">
                        <button title={p.active ? "Deactivate" : "Activate"} onClick={() => toggle(p)}>
                          <Power size={15} />
                        </button>
                        <button title="Edit" onClick={() => openEdit(p)}>
                          <Pencil size={15} />
                        </button>
                        <button title="Delete" className="danger" onClick={() => remove(p)}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="sp-overlay" onClick={closeModal}>
          <form className="sp-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
            <div className="sp-modal-head">
              <h2>{editingId ? "Edit Product" : "Add Product"}</h2>
              <button type="button" onClick={closeModal} aria-label="Close"><X size={18} /></button>
            </div>

            <div className="sp-modal-body">
              {formError && <div className="sp-alert">{formError}</div>}

              <label>Product name
                <input value={form.name} onChange={setField("name")} placeholder="e.g. Partner Basic" />
              </label>

              <div className="sp-row">
                <label>Code <em>(auto if empty)</em>
                  <input value={form.code} onChange={setField("code")} placeholder="PARTNER_BASIC" />
                </label>
                <label>Feature code <em>(auto if empty)</em>
                  <input value={form.featureCode} onChange={setField("featureCode")} placeholder={`${form.userType}_SUBSCRIPTION`} />
                </label>
              </div>

              <label>Description
                <textarea rows={2} value={form.description} onChange={setField("description")} />
              </label>

              <div className="sp-row">
                <label>For user type
                  <select value={form.userType} onChange={setField("userType")}>
                    {USER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label>Price (₹)
                  <input type="number" min="0" step="0.01" value={form.price} onChange={setField("price")} />
                </label>
              </div>

              <label>Validity (days)
                <input type="number" min="1" value={form.validityDays} onChange={setField("validityDays")} />
              </label>

              <div className="sp-row">
                <label>Job post limit
                  <input type="number" min="0" value={form.jobPostLimit} onChange={setField("jobPostLimit")} />
                </label>
                <label>Job invite limit
                  <input type="number" min="0" value={form.jobInviteLimit} onChange={setField("jobInviteLimit")} />
                </label>
                <label>Profile view limit
                  <input type="number" min="0" value={form.profileViewLimit} onChange={setField("profileViewLimit")} />
                </label>
                <label>Resume limit
                  <input type="number" min="0" value={form.resumeLimit} onChange={setField("resumeLimit")} />
                </label>
              </div>

              <label className="sp-check">
                <input type="checkbox" checked={form.active} onChange={setField("active")} />
                Active (visible to users)
              </label>
            </div>

            <div className="sp-modal-foot">
              <button type="button" className="sp-btn sp-btn-ghost" onClick={closeModal}>Cancel</button>
              <button type="submit" className="sp-btn sp-btn-primary" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Save Changes" : "Create Product"}
              </button>
            </div>
          </form>
        </div>
      )}

      {toast && <div className={`sp-toast ${toast.type}`}>{toast.text}</div>}
    </div>
  );
}

export default SubscriptionProducts;